import type { PhotoEffectId } from '@/lib/photo-effects/types/photo-effect-id'
import type { PhotoEffectDefinition } from '@/lib/photo-effects/types/photo-effect-definition'

/**
 * Full PEZ catalog (WMPCM-596). Order matches Figma Adjust list:
 * binary toggles first, then symmetric, then percent-style.
 *
 * Cartoonify default follows the majority ticket/Figma note (50). If design confirms 10 later,
 * change only `defaultValue` here.
 */
export const PHOTO_EFFECT_DEFINITIONS: readonly PhotoEffectDefinition[] = [
  { id: 'autoBrightness', label: 'Auto brightness', kind: 'binary', icon: 'wand-sparkles' },
  { id: 'autoContrast', label: 'Auto contrast', kind: 'binary', icon: 'wand-sparkles' },
  { id: 'autoColor', label: 'Auto color', kind: 'binary', icon: 'palette' },
  { id: 'grayscale', label: 'Grayscale', kind: 'binary', icon: 'circle-dot' },
  { id: 'improveIndoor', label: 'Improve - indoor', kind: 'binary', icon: 'home' },
  { id: 'improveOutdoor', label: 'Improve - outdoor', kind: 'binary', icon: 'trees' },
  { id: 'redEye', label: 'Red eye', kind: 'binary', icon: 'eye' },

  {
    id: 'brightness',
    label: 'Brightness',
    kind: 'symmetric',
    icon: 'sun',
    defaultValue: 0,
    min: -10,
    max: 10,
  },
  {
    id: 'contrast',
    label: 'Contrast',
    kind: 'symmetric',
    icon: 'contrast',
    defaultValue: 0,
    min: -10,
    max: 10,
  },
  {
    id: 'gamma',
    label: 'Gamma',
    kind: 'symmetric',
    icon: 'waves',
    defaultValue: 0,
    min: -10,
    max: 10,
  },
  {
    id: 'saturation',
    label: 'Saturation',
    kind: 'symmetric',
    icon: 'droplets',
    defaultValue: 0,
    min: -10,
    max: 10,
  },
  {
    id: 'vibrance',
    label: 'Vibrance',
    kind: 'symmetric',
    icon: 'sparkles',
    defaultValue: 0,
    min: -10,
    max: 10,
  },

  {
    id: 'blur',
    label: 'Blur',
    kind: 'percent',
    icon: 'aperture',
    defaultValue: 50,
    min: 1,
    max: 100,
  },
  {
    id: 'fillLight',
    label: 'Fill light',
    kind: 'percent',
    icon: 'lamp',
    defaultValue: 50,
    min: 1,
    max: 100,
  },
  {
    id: 'sepia',
    label: 'Sepia',
    kind: 'percent',
    icon: 'image',
    defaultValue: 50,
    min: 1,
    max: 100,
  },
  {
    id: 'sharpen',
    label: 'Sharpen',
    kind: 'percent',
    icon: 'focus',
    defaultValue: 50,
    min: 1,
    max: 100,
  },
  {
    id: 'cartoonify',
    label: 'Cartoonify',
    kind: 'percent',
    icon: 'smile',
    defaultValue: 50,
    min: 1,
    max: 100,
  },
  {
    id: 'oilPainting',
    label: 'Oil painting',
    kind: 'percent',
    icon: 'paintbrush',
    defaultValue: 50,
    min: 1,
    max: 100,
  },
]

export const PHOTO_EFFECT_BY_ID: ReadonlyMap<PhotoEffectId, PhotoEffectDefinition> = new Map(
  PHOTO_EFFECT_DEFINITIONS.map((def) => [def.id, def])
)

export function getPhotoEffectDefinition(id: PhotoEffectId): PhotoEffectDefinition {
  const def = PHOTO_EFFECT_BY_ID.get(id)

  if (!def) {
    throw new Error(`Unknown photo effect id: ${id}`)
  }

  return def
}
